import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { formatDate } from '../utils/dateUtils';
import { getToolIcon, getToolName, renderContentPreview } from '../utils/toolUtils';
import { ResponseDetail } from './ResponseDetail';

interface ResponsesTableProps {
  filteredResponses: any[];
  selectedItems: string[];
  toggleItemSelection: (id: string) => void;
  handleDelete: (id: string) => void;
}

export const ResponsesTable: React.FC<ResponsesTableProps> = ({
  filteredResponses,
  selectedItems,
  toggleItemSelection,
  handleDelete
}) => {
  const [selectedResponse, setSelectedResponse] = useState<any>(null);

  return (
    <div className="rounded-md border bg-white"> {/* Added bg-white here */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12"></TableHead>
            <TableHead className="w-12"></TableHead>
            <TableHead>Title</TableHead>
            <TableHead className="hidden md:table-cell">Content</TableHead>
            <TableHead className="w-24">Date</TableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredResponses.map((item) => (
            <TableRow key={item.id} className={selectedItems.includes(item.id) ? "bg-muted/50" : ""}>
              <TableCell>
                <Checkbox
                  checked={selectedItems.includes(item.id)}
                  onCheckedChange={() => toggleItemSelection(item.id)}
                  aria-label="Select item"
                />
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-center">
                  {getToolIcon(item.tool_type)}
                </div>
              </TableCell>
              <TableCell className="font-medium">{item.title}</TableCell>
              <TableCell className="hidden md:table-cell max-w-md truncate">
                {renderContentPreview(item)}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(item.created_at)}
              </TableCell>
              <TableCell className="text-right">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedResponse(item)}
                    >
                      View
                    </Button>
                  </SheetTrigger>
                  <SheetContent className="w-[90vw] sm:max-w-[600px] overflow-y-auto bg-white">
                    <SheetHeader>
                      <SheetTitle>{item.title}</SheetTitle>
                      <SheetDescription>
                        {getToolName(item.tool_type)} • {formatDate(item.created_at)}
                      </SheetDescription>
                    </SheetHeader>
                    <div className="mt-6">
                      <ResponseDetail item={item} />
                    </div>
                  </SheetContent>
                </Sheet>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="ml-2 text-red-500 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete this saved item. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleDelete(item.id)}
                        className="bg-red-500 hover:bg-red-600"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
